"use client";

import { useTranslations } from "next-intl";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useRef,
} from "react";
import { toast } from "@/components/ui/sonner";
import {
  mergeAddonsPricing,
  productItemsToAddons,
  productItemsToPricing,
} from "@/features/addons/lib/product-items";
import { subItemsToAddons } from "@/features/addons/lib/sub-items";
import { getAddonsErrorMessage } from "@/features/addons/lib/sub-items-error";
import { useAddonCartChangeEvents } from "@/features/analytics/hooks/use-addon-cart-change-events";
import { useEventCart } from "@/features/analytics/hooks/use-event-cart";
import { getCartItemGtmValue } from "@/features/analytics/lib/gtm-value";
import { useRemoveFromCart } from "@/features/cart/api/hooks/use-remove-from-cart";
import { useUpdateCart } from "@/features/cart/api/hooks/use-update-cart";
import { useCartStore } from "@/features/cart/components/cart-provider";
import { productItemsForItem } from "@/features/cart/helpers/product-items";
import {
  type PendingRemoval,
  useRemovalQueue,
} from "@/features/cart/hooks/use-removal-queue";

type PendingRemovalsContextValue = ReturnType<typeof useRemovalQueue>;

const PendingRemovalsContext =
  createContext<PendingRemovalsContextValue | null>(null);

export const PendingRemovalsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const t = useTranslations();
  const cartStore = useCartStore((state) => state);
  const { sendCertEvent } = useEventCart();
  const reportAddonCartChanges = useAddonCartChangeEvents();

  const { mutateAsync: removeItem } = useRemoveFromCart({
    onSuccess: ({ cartId, cart }, { itemId }) => {
      cartStore.updateCart(cartId, cart, "remove", t);
      cartStore.removeDeliveryCost(itemId);
    },
    onError: (error) => toast.error(error.message),
  });

  const inFlightAddonsRef = useRef(new Set<string>());

  const { mutateAsync: updateItem } = useUpdateCart({
    onSuccess: ({ cartId, cart }) =>
      cartStore.updateCart(cartId, cart, "update", t),
    onError: (error) => toast.error(getAddonsErrorMessage(error, t)),
  });

  const commit = useCallback(
    (removal: PendingRemoval) => {
      const cartItem = cartStore.items.find(
        (item) => item.configurationItem.id === removal.itemId,
      );
      if (!cartItem) {
        return;
      }

      if (removal.kind === "diet") {
        const daysCount = cartItem.configurationItem.dates.length || 1;

        return removeItem(
          { cartId: cartStore.cartId, itemId: removal.itemId },
          {
            onSuccess: () =>
              sendCertEvent({
                eventName: "remove_from_cart",
                diets: [
                  {
                    ...cartItem.configurationItem,
                    price: cartItem.pricing.final.gross / daysCount,
                    quantity: 1,
                    duration: daysCount,
                    subItemsPricing: cartItem.subItemsPricing,
                  },
                ],
                value: getCartItemGtmValue(cartItem),
                other: {
                  cartId: cartStore.cartId,
                },
              }),
          },
        );
      }

      const inFlight = inFlightAddonsRef.current;
      const inFlightKey = `${removal.itemId}:${removal.addonId}`;
      inFlight.add(inFlightKey);

      const isStaying = (addonId: string) =>
        !inFlight.has(`${removal.itemId}:${addonId}`);

      // The removed entry may be a catering addon (sub-item of the position)
      // or a Foodango product (the cart's product collection); both are keyed
      // by the catalog addon id, so one filter serves both (MPL-3829).
      const subItems = (cartItem.configurationItem.subItems ?? []).filter(
        (subItem) => isStaying(subItem.id),
      );
      const itemProducts = productItemsForItem(
        cartStore.productItems,
        removal.itemId,
      );
      const productItems = itemProducts
        .filter(({ productItem }) => isStaying(productItem.productId))
        .map(({ productItem }) => ({
          productId: productItem.productId,
          merchantId: productItem.merchantId,
          date: productItem.date,
          quantity: productItem.quantity,
        }));

      return updateItem(
        {
          cartId: cartStore.cartId,
          item: { ...cartItem.configurationItem, subItems },
          productItems,
        },
        {
          onSuccess: () => {
            const previous = [
              ...subItemsToAddons(cartItem.configurationItem.subItems),
              ...productItemsToAddons(itemProducts),
            ];

            reportAddonCartChanges({
              cateringName: removal.cateringName,
              categories: removal.categories,
              previous,
              next: previous.filter(
                (entry) => entry.addonId !== removal.addonId,
              ),
              // The quote from before the write still prices the addon being
              // removed, at the amount the summary showed (MPL-3873).
              pricing: mergeAddonsPricing(
                cartItem.subItemsPricing,
                productItemsToPricing(itemProducts),
              ),
            });
          },
          onSettled: () => inFlight.delete(inFlightKey),
        },
      );
    },
    [cartStore, removeItem, reportAddonCartChanges, sendCertEvent, updateItem],
  );

  const queue = useRemovalQueue(commit);

  return (
    <PendingRemovalsContext.Provider value={queue}>
      {children}
    </PendingRemovalsContext.Provider>
  );
};

export const usePendingRemovals = () => {
  const context = useContext(PendingRemovalsContext);

  if (!context) {
    throw new Error(
      "usePendingRemovals must be used within PendingRemovalsProvider",
    );
  }

  return context;
};
